/**
 * Dealer authorizations — Alex's cross-organization review queue for brand
 * authorization claims: which store, which brand, how it verifies, and the
 * proof the store sent (a file or a link) to open and judge, plus the
 * dealer-list matches Motzu recorded but did not verify on its own.
 *
 * Per-store detail still lives on Store Details (`AdminBrandAuthorizationsPanel`);
 * this page is the other direction — every claim waiting on Alex, regardless
 * of which store filed it.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { ConfirmDialog, DateField, FormDialog, InlineMessage, StatusChip } from '../../components/ui';
import {
  BrandAuthorization,
  accountSummary,
  channelsLabel,
  levelLabel,
  methodLabel,
  VERIFICATION_STATUS_META,
  verificationTagOf,
} from '../../utils/brandAuthorization';
import { bvCardSx, bvColor, bvContentSx, bvPageSx, bvRadius, bvTransition, bvType, tabular } from './brandVendorsTokens';
import { BvButton, BvEmptyState, BvPageHeader, IconArrowOut } from './components/BrandVendorsPrimitives';
import { ButtonBase } from '@mui/material';
import { ResponsiveRows, ResponsiveRowsColumn } from '../../components/ui/ResponsiveRows';
import BrandDesignationChips from '../../components/brandAuthorizations/BrandDesignationChips';

const visuallyHidden = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' } as const;

type QueueRecord = BrandAuthorization & { organizationName: string };

// Column widths mirror the old fixed-px `AUTH_QUEUE_GRID`; `ResponsiveRows`
// only applies them from `md` up (see docs/internal/MOBILE_UI_ASSESSMENT.md — the
// Admin -> Plans bug this pattern avoids).
const AUTH_QUEUE_COLUMNS: ResponsiveRowsColumn[] = [
  { key: 'store', header: 'Store', width: 'minmax(160px,1.3fr)', mobileLabel: true },
  { key: 'brand', header: 'Brand', width: 'minmax(140px,1fr)', mobileLabel: true },
  { key: 'method', header: 'How it verifies', width: 'minmax(160px,1.4fr)', mobileLabel: true },
  { key: 'proof', header: 'Proof', width: 'minmax(160px,1.4fr)', mobileLabel: true },
  { key: 'actions', header: 'Actions', width: 'minmax(220px,1.6fr)' },
];

type QueueStatus = 'pending' | 'list_match' | 'unverified' | 'expired' | 'rejected' | 'verified';

const STATUS_PILLS: Array<{ id: QueueStatus; label: string }> = [
  { id: 'pending', label: 'Pending review' },
  { id: 'list_match', label: 'List matches' },
  { id: 'unverified', label: 'Unverified' },
  { id: 'expired', label: 'Expired' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'verified', label: 'Verified' },
];

/** The list row Motzu found for a claim it did not verify on its own. */
const listMatchLine = (record: QueueRecord): string | null => {
  const stale = record.verification.staleListMatch;
  if (stale) {
    return `Older list${stale.listAge ? ` (${stale.listAge} days)` : ''}: ${stale.dealerName || 'unnamed row'}${stale.city ? `, ${stale.city}` : ''}${stale.state ? ` ${stale.state}` : ''}`;
  }
  const listed = record.directoryMatch;
  if (!listed || listed.confidence === 'none') return null;
  const where = `${listed.dealerName || 'unnamed row'}${listed.city ? `, ${listed.city}` : ''}${listed.state ? ` ${listed.state}` : ''}`;
  return listed.confidence === 'weak'
    ? `Partial name match: ${where}`
    : `Exact match held after a store identity change: ${where}`;
};

async function readError(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => ({}));
  return body?.error?.message || body?.message || fallback;
}

/** A store that declared itself an unauthorized dealer and sent nothing: there is no claim to verify. */
const nothingToVerify = (record: QueueRecord): boolean => record.level === 'unauthorized' && !record.proof;

/** A claim with no uploaded proof, or one the store only declared itself. */
const lacksProof = (record: QueueRecord): boolean =>
  (!record.proof && !record.proofLink) || record.verification.method === 'self_declared';

export const BrandVendorsDealerAuthorizationsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<QueueStatus>('pending');
  // Until the admin picks a tab themselves, land on the first one with work in it.
  const pickedTab = useRef(false);
  const [verifyTarget, setVerifyTarget] = useState<QueueRecord | null>(null);
  const [noProofTarget, setNoProofTarget] = useState<QueueRecord | null>(null);
  const [rejectTarget, setRejectTarget] = useState<QueueRecord | null>(null);
  const [expiresAt, setExpiresAt] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const queryKey = ['admin-brand-authorizations-queue', status];
  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await makeApiCall(`/admin/brand-authorizations?status=${status}`, {
        headers: await getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await readError(response, 'Failed to load the authorization queue'));
      const body = await response.json();
      return body as { data: QueueRecord[]; count: number };
    },
    staleTime: 30_000,
  });

  const countsQuery = useQuery({
    queryKey: ['admin-brand-authorizations-counts'],
    queryFn: async () => {
      const response = await makeApiCall('/admin/brand-authorizations/counts', { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await readError(response, 'Failed to load queue counts'));
      const body = await response.json();
      return body.data as Record<QueueStatus, number>;
    },
    staleTime: 30_000,
  });
  const counts = countsQuery.data;

  useEffect(() => {
    if (!counts || pickedTab.current) return;
    const firstWithWork = STATUS_PILLS.find(pill => counts[pill.id] > 0);
    if (firstWithWork && counts[status] === 0) setStatus(firstWithWork.id);
  }, [counts, status]);

  // Grouped by store so a store's name appears once, then its brands beneath.
  const records = useMemo(
    () =>
      [...(data?.data || [])].sort(
        (a, b) =>
          a.organizationName.localeCompare(b.organizationName) || a.brandName.localeCompare(b.brandName),
      ),
    [data],
  );
  const firstOfStore = useMemo(() => {
    const seen = new Set<string>();
    const firsts = new Set<string>();
    for (const record of records) {
      if (!seen.has(record.organizationId)) {
        seen.add(record.organizationId);
        firsts.add(record.id);
      }
    }
    return firsts;
  }, [records]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-brand-authorizations-queue'] });
    queryClient.invalidateQueries({ queryKey: ['admin-brand-authorizations-counts'] });
  };

  const resetDialogs = () => {
    setVerifyTarget(null);
    setNoProofTarget(null);
    setRejectTarget(null);
    setExpiresAt('');
    setReviewNotes('');
    setRejectReason('');
  };

  const verifyMutation = useMutation({
    mutationFn: async () => {
      if (!verifyTarget) throw new Error('Nothing to verify');
      const response = await makeApiCall(
        `/admin/brand-authorizations/organizations/${verifyTarget.organizationId}/${encodeURIComponent(verifyTarget.brandId)}/verify`,
        {
          method: 'POST',
          headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            method: 'dealer_letter',
            expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
            notes: reviewNotes.trim() || undefined,
          }),
        },
      );
      if (!response.ok) throw new Error(await readError(response, 'Failed to verify'));
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['admin-brand-authorizations', verifyTarget?.organizationId] });
      resetDialogs();
    },
  });

  const approveMatchMutation = useMutation({
    mutationFn: async (record: QueueRecord) => {
      const response = await makeApiCall(
        `/admin/brand-authorizations/organizations/${record.organizationId}/${encodeURIComponent(record.brandId)}/approve-list-match`,
        {
          method: 'POST',
          headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        },
      );
      if (!response.ok) throw new Error(await readError(response, 'Failed to approve the list match'));
      return record;
    },
    onSuccess: record => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['admin-brand-authorizations', record.organizationId] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      if (!rejectTarget) throw new Error('Nothing to reject');
      if (!rejectReason.trim()) throw new Error('Tell the dealer why the proof was rejected');
      const response = await makeApiCall(
        `/admin/brand-authorizations/organizations/${rejectTarget.organizationId}/${encodeURIComponent(rejectTarget.brandId)}/reject`,
        {
          method: 'POST',
          headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes: rejectReason.trim() }),
        },
      );
      if (!response.ok) throw new Error(await readError(response, 'Failed to reject'));
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['admin-brand-authorizations', rejectTarget?.organizationId] });
      resetDialogs();
    },
  });

  return (
    <Box sx={bvPageSx}>
      <Box sx={bvContentSx}>
        <BvPageHeader
          title="Dealer authorizations"
          description={
            <>
              Every brand authorization claim waiting on review, across every store. Open the uploaded proof, then
              verify or reject. Per-store detail and the admin-assisted upload path still live on{' '}
              <Box component={RouterLink} to="/app/admin/stores" sx={{ color: bvColor.accent, '&:hover': { color: bvColor.accentHover } }}>
                Store Details
              </Box>
              .
            </>
          }
        />

        <Box role="group" aria-label="Review queue" sx={{ display: 'flex', gap: '8px', alignItems: 'center', mt: '22px', flexWrap: 'wrap' }}>
          {STATUS_PILLS.map(pill => {
            const on = status === pill.id;
            return (
              <ButtonBase
                key={pill.id}
                aria-pressed={on}
                onClick={() => {
                  pickedTab.current = true;
                  setStatus(pill.id);
                }}
                sx={{
                  height: 32,
                  px: '13px',
                  gap: '7px',
                  borderRadius: `${bvRadius.pill}px`,
                  ...bvType.button,
                  color: on ? bvColor.accentHover : bvColor.ink3,
                  bgcolor: on ? bvColor.surfaceTint : bvColor.surface,
                  border: `1px solid ${on ? bvColor.accent : bvColor.border}`,
                  transition: bvTransition,
                  '&:hover': { borderColor: bvColor.accent },
                  '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
                }}
              >
                {pill.label}
                <Box component="span" sx={{ color: bvColor.muted, ...tabular }}>
                  {counts ? counts[pill.id] : '–'}
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
        <Box sx={{ ...bvType.caption, color: bvColor.ink3, mt: '10px' }}>
          <strong>Pending review</strong> claims have proof the store sent and are waiting on you.{' '}
          <strong>List matches</strong> were found on the brand&rsquo;s dealer list but not verified on their own (a
          partial spelling, a list older than 180 days, or a store that changed its name or address): approve in one
          click. <strong>Unverified</strong> claims have no proof under review yet.{' '}
          <strong>Expired</strong> claims were verified and lapsed, so the store has to re-verify.
        </Box>

        {approveMatchMutation.isError && (
          <Box sx={{ mt: 2 }}>
            <InlineMessage severity="error">{(approveMatchMutation.error as Error).message}</InlineMessage>
          </Box>
        )}

        {isError && (
          <Box sx={{ mt: 2 }}>
            <InlineMessage severity="error">
              {(error as Error)?.message || 'Failed to load the authorization queue'}
            </InlineMessage>
          </Box>
        )}

        {!isError && (
          <Box sx={{ ...bvCardSx, mt: '16px', overflowX: 'auto' }}>
            <ResponsiveRows
              columns={AUTH_QUEUE_COLUMNS}
              rows={records}
              getRowKey={record => record.id}
              isLoading={isLoading}
              gap="12px"
              padding="14px 16px"
              headerPadding="10px 16px"
              headerCellSx={{ fontSize: 10.5, letterSpacing: '0.08em', color: bvColor.ink3 }}
              rowDivider="bottom"
              emptyState={
                <BvEmptyState
                  headline="Nothing waiting"
                  note={`No brand authorizations are ${STATUS_PILLS.find(p => p.id === status)?.label.toLowerCase() || status} right now.`}
                />
              }
              renderCell={(record, columnKey) => {
                const meta = VERIFICATION_STATUS_META[record.effectiveStatus || record.verification.status];
                switch (columnKey) {
                  case 'store':
                    return (
                      firstOfStore.has(record.id) ? (
                        <Box sx={{ ...bvType.tablePrimary, color: bvColor.ink }}>{record.organizationName}</Box>
                      ) : (
                        <Box sx={{ ...bvType.caption, color: bvColor.muted }}>
                          <Box component="span" sx={visuallyHidden}>{record.organizationName}</Box>
                          <span aria-hidden>↳ same store</span>
                        </Box>
                      )
                    );
                  case 'brand':
                    return (
                      <>
                        <Box sx={{ ...bvType.body, color: bvColor.ink }}>{record.brandName}</Box>
                        <Box sx={{ ...bvType.caption, color: bvColor.ink3, mb: '4px' }}>
                          {[levelLabel(record.level), channelsLabel(record.channels), accountSummary(record)].filter(Boolean).join(' · ')}
                        </Box>
                        <StatusChip label={meta.label} tone={meta.tone} variant="outlined" />
                        {record.designations && record.designations.length > 0 && (
                          <Box sx={{ mt: '6px' }}>
                            <BrandDesignationChips designations={record.designations} />
                          </Box>
                        )}
                      </>
                    );
                  case 'method':
                    return (
                      <>
                        <Box sx={{ ...bvType.body, color: bvColor.ink }}>{methodLabel(record.verification.method)}</Box>
                        {record.verification.sourceEmail && (
                          <Box sx={{ ...bvType.caption, color: bvColor.ink3 }}>{record.verification.sourceEmail}</Box>
                        )}
                        {verificationTagOf(record).kind === 'list_match' && listMatchLine(record) && (
                          <Box sx={{ ...bvType.caption, color: bvColor.ink3 }}>{listMatchLine(record)}</Box>
                        )}
                        {record.verification.reviewNotes && (
                          <Box sx={{ ...bvType.caption, color: bvColor.ink3 }}>Note: {record.verification.reviewNotes}</Box>
                        )}
                      </>
                    );
                  case 'proof': {
                    const linkSx = {
                      ...bvType.caption,
                      color: bvColor.ink,
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    };
                    const storeNote = record.proofNote ? (
                      <Box sx={{ ...bvType.caption, color: bvColor.ink3 }}>Store's note: {record.proofNote}</Box>
                    ) : null;
                    if (!record.proof && !record.proofLink) {
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <Box sx={{ ...bvType.caption, color: bvColor.ink3 }}>No proof on file</Box>
                          {storeNote}
                        </Box>
                      );
                    }
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {record.proof &&
                          (record.proof.url ? (
                            <Box component="a" href={record.proof.url} target="_blank" rel="noreferrer" sx={linkSx}>
                              {record.proof.fileName}
                              <IconArrowOut size={12} />
                            </Box>
                          ) : (
                            <Box sx={{ ...bvType.caption, color: bvColor.ink3 }}>{record.proof.fileName}</Box>
                          ))}
                        {record.proofLink && (
                          <Box component="a" href={record.proofLink} target="_blank" rel="noreferrer" sx={linkSx}>
                            Link the store sent
                            <IconArrowOut size={12} />
                          </Box>
                        )}
                        {storeNote}
                      </Box>
                    );
                  }
                  default:
                    return (
                      <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {verificationTagOf(record).kind === 'list_match' && (
                          <BvButton
                            size="sm"
                            tone="primary"
                            disabled={approveMatchMutation.isPending}
                            onClick={() => approveMatchMutation.mutate(record)}
                          >
                            {approveMatchMutation.isPending && approveMatchMutation.variables?.id === record.id
                              ? 'Approving…'
                              : 'Approve match'}
                          </BvButton>
                        )}
                        {/* Without proof, Verify is demoted to the neutral style and gated behind a confirm step. */}
                        <BvButton
                          size="sm"
                          tone={lacksProof(record) ? 'default' : 'primary'}
                          disabled={nothingToVerify(record)}
                          disabledReason="This store declared itself an unauthorized dealer for this brand and has sent no proof, so there is nothing to verify. Reject the claim, or wait for the store to send proof."
                          onClick={() => {
                            resetDialogs();
                            if (lacksProof(record)) {
                              setNoProofTarget(record);
                            } else {
                              setVerifyTarget(record);
                            }
                          }}
                        >
                          Verify
                        </BvButton>
                        <BvButton
                          size="sm"
                          tone="danger"
                          onClick={() => {
                            resetDialogs();
                            setRejectTarget(record);
                          }}
                        >
                          Reject
                        </BvButton>
                      </Box>
                    );
                }
              }}
            />
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={!!noProofTarget}
        title="Verify without proof?"
        confirmColor="warning"
        message={
          noProofTarget
            ? `No proof is on file for the ${noProofTarget.brandName} claim from ${noProofTarget.organizationName}. ` +
              `${noProofTarget.verification.method === 'self_declared' ? 'The store only declared this authorization itself. ' : ''}` +
              'Verifying it marks the authorization as confirmed without any supporting document.'
            : ''
        }
        confirmLabel="Continue to verify"
        onConfirm={() => {
          const target = noProofTarget;
          resetDialogs();
          setVerifyTarget(target);
        }}
        onClose={resetDialogs}
      />

      <FormDialog
        open={!!verifyTarget}
        title={verifyTarget ? `Verify ${verifyTarget.brandName} for ${verifyTarget.organizationName}` : ''}
        onClose={resetDialogs}
        onSubmit={() => verifyMutation.mutate()}
        submitLabel={verifyMutation.isPending ? 'Verifying…' : 'Verify'}
        submitDisabled={verifyMutation.isPending}
      >
        {verifyMutation.isError && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {(verifyMutation.error as Error).message}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Confirms the proof on file shows the store is a genuine {verifyTarget?.brandName} dealer. Optional expiry
          and review notes.
        </Typography>
        <DateField
          label="Expires on (optional)"
          value={expiresAt}
          onChange={setExpiresAt}
          size="medium"
          sx={{ width: '100%', mb: 2 }}
        />
        <TextField
          label="Review notes (optional)"
          value={reviewNotes}
          onChange={e => setReviewNotes(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
      </FormDialog>

      <ConfirmDialog
        open={!!rejectTarget}
        title="Reject this proof"
        confirmColor="error"
        message={
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {rejectTarget ? `Reject the ${rejectTarget.brandName} proof from ${rejectTarget.organizationName}?` : ''}
            </Typography>
            <TextField
              label="Reason (required — the store will see this)"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              autoFocus
            />
            {rejectMutation.isError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {(rejectMutation.error as Error).message}
              </Typography>
            )}
          </Box>
        }
        confirmLabel={rejectMutation.isPending ? 'Rejecting…' : 'Reject'}
        loading={rejectMutation.isPending}
        onConfirm={() => rejectMutation.mutate()}
        onClose={resetDialogs}
      />
    </Box>
  );
};

export default BrandVendorsDealerAuthorizationsPage;
